import { msg, Trans } from '@lingui/macro'
import { useLingui } from '@lingui/react'
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from '@mui/material'

/**
 * @param {{ rows: Array<{ year: number, total: number, fatal: number, seriousInjury: number, slightInjury: number, noInjury: number, unknown: number }>, isLoading?: boolean }} props
 */
export default function DashboardSummaryTable({ rows, isLoading = false }) {
  const { _ } = useLingui()
  const theme = useTheme()
  const headerColor =
    theme.palette.mode === 'light'
      ? theme.palette.newColors.gray
      : theme.palette.newColors.lightGray

  const columns = [
    { key: 'year', label: _(msg`Year`) },
    { key: 'total', label: _(msg`Total crashes`) },
    { key: 'fatal', label: _(msg`Fatal`) },
    { key: 'seriousInjury', label: _(msg`Serious injury`) },
    { key: 'slightInjury', label: _(msg`Slight injury`) },
    { key: 'noInjury', label: _(msg`No injury`) },
    { key: 'unknown', label: _(msg`Unknown`) },
  ]

  const columnWidth = `${(100 / columns.length).toFixed(4)}%`

  return (
    <Box sx={{ width: '100%', mb: 3 }}>
      <Typography
        variant="h6"
        sx={{
          fontWeight: 700,
          mb: 1.5,
          color: headerColor,
        }}
      >
        <Trans>Yearly crash summary</Trans>
      </Typography>
      <TableContainer component={Paper} variant="outlined">
        <Table
          size="small"
          aria-label={_(msg`Yearly crash summary table`)}
          sx={{ tableLayout: 'fixed', width: '100%' }}
        >
          <TableHead>
            <TableRow>
              {columns.map((col) => (
                <TableCell
                  key={col.key}
                  align="center"
                  sx={{
                    width: columnWidth,
                    fontWeight: 700,
                    color: headerColor,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {col.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={columns.length} align="center">
                  <Trans>Loading…</Trans>
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length} align="center">
                  <Trans>No data</Trans>
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => (
                <TableRow key={row.year} hover>
                  {columns.map((col) => (
                    <TableCell
                      key={col.key}
                      align="center"
                      sx={{
                        width: columnWidth,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {row[col.key]?.toLocaleString?.() ?? row[col.key]}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  )
}
